import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Search, Plus, Edit2, Trash2, Eye, X, Check, CheckCircle, XCircle,
  Code, Smartphone, Globe, TrendingUp, Video, Share2,
  Palette, Database, Shield, Zap, Award, Target,
  Lightbulb, Rocket, Monitor, Package, Settings, Star
} from 'lucide-react';
import * as LucideIcons from "lucide-react";

// Lucide Icon Helpers
const IconRenderer = ({ name, size = 20, className = "" }) => {
  const Icon = LucideIcons[name] || Code;
  return <Icon size={size} className={className} />;
};

const ManageServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  // Form State
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    icon: "Code",
    link: "",
    status: true
  });

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4100";

  // Available Icons for Selection
  const availableIcons = [
    "Code", "Smartphone", "Globe", "TrendingUp", "Video", "Share2",
    "Palette", "Database", "Shield", "Zap", "Award", "Target",
    "Lightbulb", "Rocket", "Monitor", "Package", "Settings"
  ];

  // Presets
  const servicePresets = [
    "Web Development", "App Development", "Digital Marketing", "UI/UX Design",
    "SEO Optimization", "Cloud Solutions", "Cybersecurity", "Data Analytics"
  ];

  // ==================== FETCH DATA ====================
  const fetchServices = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/admin/manageservices/getservices`, {
        withCredentials: true
      });
      if (res.data.success) {
        setServices(res.data.data);
      }
    } catch (error) {
      console.error("Error fetching services:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  // ==================== HANDLERS ====================
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const openAddModal = () => {
    setIsEditing(false);
    setFormData({ title: "", description: "", icon: "Code", link: "", status: true });
    setShowModal(true);
  };

  const openEditModal = (service) => {
    setIsEditing(true);
    setFormData({ ...service });
    setSelectedService(service);
    setShowModal(true);
  };

  const handleView = (service) => {
    setSelectedService(service);
    setViewModal(true);
  };

  // ==================== API ACTIONS ====================
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.description) return alert("Title and Description are required");

    try {
      let res;
      if (isEditing) {
        res = await axios.put(`${API_URL}/admin/manageservices/updateservice/${selectedService._id}`, formData, { withCredentials: true });
      } else {
        res = await axios.post(`${API_URL}/admin/manageservices/addservice`, formData, { withCredentials: true });
      }

      if (res.data.success) {
        fetchServices();
        setShowModal(false);
        alert(isEditing ? "Service Updated!" : "Service Added!");
      }
    } catch (error) {
      console.error("Error saving service:", error);
      alert(error.response?.data?.message || "Failed to save service");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this service?")) return;

    try {
      const res = await axios.delete(`${API_URL}/admin/manageservices/deleteservice/${id}`, { withCredentials: true });
      if (res.data.success) {
        fetchServices();
      }
    } catch (error) {
      console.error("Error deleting service:", error);
      alert("Failed to delete");
    }
  };

  const handleToggleStatus = async (service) => {
    try {
      // Optimistic Update
      const updatedServices = services.map(s =>
        s._id === service._id ? { ...s, status: !s.status } : s
      );
      setServices(updatedServices);

      // API Call using the specific toggle route
      await axios.patch(`${API_URL}/admin/manageservices/togglestatus/${service._id}`, {}, { withCredentials: true });

    } catch (error) {
      console.error("Error toggling status:", error);
      fetchServices(); // Revert on error
      alert("Failed to update status");
    }
  };

  // ==================== FILTERING ====================
  const filteredServices = services.filter(s =>
    s.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50 p-8 font-sans text-slate-900">

      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight">Manage Services</h1>
          <p className="text-slate-500 mt-1">Add, edit, and organize your service offerings</p>
        </div>
        <button
          onClick={openAddModal}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-indigo-500/20 transition-all transform hover:scale-105"
        >
          <Plus size={20} /> Add New Service
        </button>
      </div>

      {/* Search Bar */}
      <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-200 mb-6 flex items-center gap-3">
        <Search className="text-slate-400" size={20} />
        <input
          type="text"
          placeholder="Search services..."
          className="flex-1 outline-none text-slate-700 placeholder-slate-400"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Services Table */}
      <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400">Loading services...</div>
        ) : filteredServices.length === 0 ? (
          <div className="p-12 text-center text-slate-400">No services found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-bold">
                  <th className="p-6">Icon</th>
                  <th className="p-6">Service Info</th>
                  <th className="p-6">Status</th>
                  <th className="p-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredServices.map(service => (
                  <tr key={service._id} className="hover:bg-slate-50/50 transition-colors group">
                    <td className="p-6">
                      <div className="w-12 h-12 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
                        <IconRenderer name={service.icon} size={24} />
                      </div>
                    </td>
                    <td className="p-6">
                      <h3 className="font-bold text-slate-800 text-base mb-1">{service.title}</h3>
                      <p className="text-slate-500 text-sm line-clamp-1 max-w-xs">{service.description}</p>
                    </td>
                    <td className="p-6">
                      <button
                        onClick={() => handleToggleStatus(service)}
                        className={`px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 transition-colors ${service.status
                            ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                            : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                          }`}
                      >
                        {service.status ? <CheckCircle size={14} /> : <XCircle size={14} />}
                        {service.status ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td className="p-6 text-right">
                      <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => handleView(service)} className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="View">
                          <Eye size={18} />
                        </button>
                        <button onClick={() => openEditModal(service)} className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors" title="Edit">
                          <Edit2 size={18} />
                        </button>
                        <button onClick={() => handleDelete(service._id)} className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" title="Delete">
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ADD/EDIT MODAL */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h3 className="font-bold text-xl text-slate-800">{isEditing ? "Edit Service" : "Add New Service"}</h3>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">

              {/* Title Selection */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Service Title</label>
                <div className="relative">
                  <input
                    list="presets"
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-semibold text-slate-700"
                    placeholder="e.g. Web Development"
                  />
                  <datalist id="presets">
                    {servicePresets.map(preset => <option key={preset} value={preset} />)}
                  </datalist>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-slate-700 min-h-[100px]"
                  placeholder="Briefly describe the service..."
                />
              </div>

              {/* Icon Selection */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Select Icon</label>
                <div className="grid grid-cols-6 gap-2">
                  {availableIcons.map(iconName => (
                    <button
                      key={iconName}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, icon: iconName }))}
                      className={`p-2 rounded-lg flex items-center justify-center transition-all ${formData.icon === iconName
                          ? "bg-indigo-600 text-white shadow-md shadow-indigo-500/30 scale-110"
                          : "bg-slate-50 text-slate-400 hover:bg-slate-100 hover:scale-105"
                        }`}
                      title={iconName}
                    >
                      <IconRenderer name={iconName} size={20} />
                    </button>
                  ))}
                </div>
              </div>

              {/* Link */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Link (Optional)</label>
                <input
                  type="text"
                  name="link"
                  value={formData.link}
                  onChange={handleInputChange}
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-slate-700"
                  placeholder="/services/web-dev"
                />
              </div>

              {/* Status */}
              <label className="flex items-center gap-3 cursor-pointer group">
                <div className={`w-12 h-7 rounded-full p-1 transition-colors duration-300 ${formData.status ? "bg-emerald-500" : "bg-slate-200"}`}>
                  <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-300 ${formData.status ? "translate-x-5" : ""}`}></div>
                </div>
                <input
                  type="checkbox"
                  name="status"
                  checked={formData.status}
                  onChange={handleInputChange}
                  className="hidden"
                />
                <span className="font-semibold text-slate-700 group-hover:text-indigo-600 transition-colors">
                  {formData.status ? "Active (Visible)" : "Inactive (Hidden)"}
                </span>
              </label>

              <div className="pt-4 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-3 bg-slate-100 text-slate-600 rounded-xl font-bold hover:bg-slate-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 shadow-lg shadow-indigo-500/20 transition-all transform hover:scale-[1.02]"
                >
                  {isEditing ? "Save Changes" : "Create Service"}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

      {/* VIEW MODAL (Simple Read-Only) */}
      {viewModal && selectedService && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-8 max-w-sm w-full text-center shadow-2xl relative">
            <button onClick={() => setViewModal(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600">
              <X size={24} />
            </button>
            <div className="w-20 h-20 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center mx-auto mb-6">
              <IconRenderer name={selectedService.icon} size={40} />
            </div>
            <h3 className="text-2xl font-black text-slate-800 mb-2">{selectedService.title}</h3>
            <p className="text-slate-500 mb-6">{selectedService.description}</p>
            {selectedService.link && (
              <p className="text-xs text-indigo-500 font-mono bg-indigo-50 p-2 rounded mb-6 break-all">
                Link: {selectedService.link}
              </p>
            )}
            <div className="px-4 py-2 bg-slate-100 rounded-lg inline-block text-sm font-bold text-slate-600">
              {selectedService.status ? "Active Service" : "Inactive Service"}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ManageServices;